import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, TOKEN_KEY } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadMe = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setUser(null); setLoading(false); return; }
    try {
      const u = await api.me();
      setUser(u);
    } catch (e) {
      localStorage.removeItem(TOKEN_KEY);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadMe(); }, [loadMe]);

  useEffect(() => {
    const handler = () => { localStorage.removeItem(TOKEN_KEY); setUser(null); };
    window.addEventListener("auth:unauthorized", handler);
    return () => window.removeEventListener("auth:unauthorized", handler);
  }, []);

  const login = async (email, password) => {
    const data = await api.login(email, password);
    localStorage.setItem(TOKEN_KEY, data.access_token);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
  };

  const role = user?.role || null;
  const isFull = role === "admin" || role === "leader"; // CEO / Leaders
  const isManager = role === "manager";
  const isExecutive = role === "executive" || role === "member";
  const isAdmin = isFull; // backward-compat (admin routes = full access)
  const isCmsAdmin = role === "admin"; // PART 8 — CMS/inline-edit is ADMIN-ONLY
  const assignedVerticals = user?.assigned_verticals || [];
  // Vertical leads only see the dashboard if an admin has granted access.
  const dashboardAccess = isManager ? !!user?.dashboard_access : true;

  // Managers edit only their verticals; executives can data-enter anywhere; full = all.
  const canEditVertical = useCallback((vertical) => {
    if (isFull || isExecutive) return true;
    if (isManager) return assignedVerticals.includes(vertical);
    return false;
  }, [isFull, isExecutive, isManager, assignedVerticals]);

  // Config = budgets, targets, campaigns, users, lists (executives blocked)
  const canEditConfig = isFull || isManager;

  return (
    <AuthContext.Provider value={{
      user, loading, login, logout,
      role, isAdmin, isFull, isManager, isExecutive, isCmsAdmin,
      assignedVerticals, canEditVertical, canEditConfig, dashboardAccess,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
